import { Link } from 'react-router-dom';
import Footer from '@/pages/home/components/Footer';

export default function ArticlePage() {
  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Superbuy Spreadsheet: The Complete Guide to Organizing Your Cross-Border Shopping Workflow',
    description: 'Learn how to use a superbuy spreadsheet to track products, compare prices, manage shipping, and streamline your entire buying process from China.',
    author: { '@type': 'Organization', name: 'Superbuy Team' },
    datePublished: '2026-07-13',
    dateModified: '2026-07-13',
    publisher: { '@type': 'Organization', name: 'Superbuy Spreadsheet', url: 'https://superbuyspreadsheet.buzz' },
    mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://superbuyspreadsheet.buzz/article/superbuy-spreadsheet-ultimate-guide' },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'Do I need to pay for a superbuy spreadsheet template?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'No. You can create your own superbuy spreadsheet for free using Google Sheets or any spreadsheet application. Many community members share free templates that you can adapt to your needs.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can I share my superbuy spreadsheet with my buying agent?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Absolutely. Share a view-only or comment-access version with your agent so they can see exactly which products you want, including sizes, colors, and any special instructions.',
        },
      },
      {
        '@type': 'Question',
        name: 'How many products can a superbuy spreadsheet handle?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Google Sheets supports up to 10 million cells, so a single superbuy spreadsheet can comfortably handle thousands of products across multiple sheets.',
        },
      },
      {
        '@type': 'Question',
        name: 'What is the difference between a superbuy sheet and a regular wishlist?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'A wishlist is a simple collection of desired items. A superbuy sheet includes structured data making it a complete project management tool for cross-border shopping.',
        },
      },
      {
        '@type': 'Question',
        name: 'Is a superbuy spreadsheet useful for small hauls?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes. Even with just three to five items, a superbuy spreadsheet helps you stay organized and avoid mistakes, especially when ordering from multiple sellers.',
        },
      },
    ],
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://superbuyspreadsheet.buzz/' },
      { '@type': 'ListItem', position: 2, name: 'Ultimate Guide', item: 'https://superbuyspreadsheet.buzz/article/superbuy-spreadsheet-ultimate-guide' },
    ],
  };

  return (
    <main>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <span className="text-lg font-heading font-bold text-foreground-950">Superbuy</span>
            <span className="text-lg font-heading font-light text-foreground-500">Spreadsheet</span>
          </Link>
          <Link
            to="/"
            className="whitespace-nowrap text-sm font-medium text-foreground-600 hover:text-foreground-950 transition-colors duration-200 inline-flex items-center gap-1.5"
          >
            <i className="ri-arrow-left-line" />
            Back to Home
          </Link>
        </div>
      </nav>

      {/* Article Header */}
      <header className="bg-background-50 pt-12 pb-8 md:pt-20 md:pb-12">
        <div className="max-w-[800px] mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3 mb-5">
            <span className="px-3 py-1 bg-accent-100 text-accent-700 text-xs font-semibold rounded-full uppercase tracking-wide">
              Ultimate Guide
            </span>
            <span className="text-xs text-foreground-400">12 min read</span>
          </div>

          <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground-950 leading-[1.08] mb-5">
            Superbuy Spreadsheet: The Complete Guide to Organizing Your Cross-Border Shopping Workflow
          </h1>

          <p className="text-base md:text-lg text-foreground-500 leading-relaxed mb-8 max-w-2xl">
            Learn how to use a superbuy spreadsheet to track products, compare prices, manage shipping,
            and streamline your entire buying process from China. This comprehensive guide covers
            everything from setup to advanced optimization.
          </p>

          <div className="flex items-center gap-4 pb-8 border-b border-background-200">
            <div className="w-12 h-12 rounded-full bg-accent-100 flex items-center justify-center">
              <span className="text-accent-600 font-bold text-sm">SS</span>
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground-800">Superbuy Team</p>
              <p className="text-xs text-foreground-400">
                Published July 13, 2026 &middot; Last updated July 13, 2026
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Article Body */}
      <article className="bg-background-50 pb-20">
        <div className="max-w-[800px] mx-auto px-6 md:px-10">
          <div className="prose-custom text-foreground-700 leading-relaxed space-y-8">

            {/* Quick Answer Box */}
            <div className="bg-background-100 border border-background-200 rounded-2xl p-6 md:p-8">
              <h2 className="text-lg font-heading font-bold text-foreground-950 mb-3">Quick Answer</h2>
              <p className="text-sm text-foreground-600">
                A <strong>superbuy spreadsheet</strong> is a structured tracking system that helps cross-border
                shoppers organize product listings from Chinese marketplaces. It consolidates product names, URLs, prices,
                shipping estimates, agent fees, size charts, and order status into a single organized document — saving
                hours of manual searching and reducing costly ordering mistakes.
              </p>
            </div>

            {/* TOC */}
            <nav className="bg-background-100 rounded-2xl p-6 md:p-8" aria-label="Table of Contents">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-foreground-500 mb-4">Table of Contents</h2>
              <ol className="space-y-2 text-sm">
                <li><a href="#what-is" className="text-foreground-600 hover:text-foreground-950 transition-colors">1. What Is a Superbuy Spreadsheet?</a></li>
                <li><a href="#why-use" className="text-foreground-600 hover:text-foreground-950 transition-colors">2. Why You Need a Superbuy Spreadsheet</a></li>
                <li><a href="#setup" className="text-foreground-600 hover:text-foreground-950 transition-colors">3. How to Set Up Your Superbuy Spreadsheet</a></li>
                <li><a href="#essential-columns" className="text-foreground-600 hover:text-foreground-950 transition-colors">4. Essential Columns & Fields</a></li>
                <li><a href="#pricing" className="text-foreground-600 hover:text-foreground-950 transition-colors">5. Managing Pricing & Shipping Costs</a></li>
                <li><a href="#tracking" className="text-foreground-600 hover:text-foreground-950 transition-colors">6. Order Tracking & Status Management</a></li>
                <li><a href="#advanced" className="text-foreground-600 hover:text-foreground-950 transition-colors">7. Advanced Tips & Automation</a></li>
                <li><a href="#mistakes" className="text-foreground-600 hover:text-foreground-950 transition-colors">8. Common Mistakes to Avoid</a></li>
                <li><a href="#faq" className="text-foreground-600 hover:text-foreground-950 transition-colors">9. Frequently Asked Questions</a></li>
              </ol>
            </nav>

            {/* Section 1 */}
            <section id="what-is">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                What Is a Superbuy Spreadsheet?
              </h2>
              <p className="text-sm md:text-base mb-4">
                A <strong>superbuy spreadsheet</strong> is far more than a simple product list. It is a dedicated
                organizational system designed specifically for cross-border shoppers who source products from
                Chinese e-commerce platforms such as Weidian, Taobao, and 1688. The spreadsheet acts as your
                central command center, bringing together every piece of information you need to make informed
                purchasing decisions.
              </p>
              <p className="text-sm md:text-base mb-4">
                When you browse through thousands of product listings — shoes, hoodies, t-shirts, jackets, and
                accessories — it is easy to lose track of which items you have already reviewed, which ones fit
                your budget, and which sellers offer the best quality. A well-structured superbuy spreadsheet
                solves this problem by giving every product a dedicated row with standardized columns for quick
                comparison.
              </p>
              <p className="text-sm md:text-base">
                The term <strong>superbuy sheet</strong> comes from the community of shoppers who use buying
                agents to purchase products from China. These shoppers needed a systematic way to communicate
                with their agents, track orders across multiple platforms, and calculate total costs including
                domestic shipping, international freight, and agent service fees. Over time, the superbuy
                spreadsheet evolved into an indispensable tool used by thousands of cross-border shoppers
                worldwide.
              </p>
            </section>

            {/* Section 2 */}
            <section id="why-use">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                Why You Need a Superbuy Spreadsheet
              </h2>
              <p className="text-sm md:text-base mb-4">
                Cross-border shopping without a tracking system is chaotic. You might find yourself scrolling
                through endless chat histories trying to remember which seller had that specific pair of sneakers,
                or guessing whether an item has already been shipped to your agent&apos;s warehouse. A superbuy
                spreadsheet eliminates all of that guesswork.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
                <div className="bg-background-100 rounded-xl p-5">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 mb-3">
                    <i className="ri-time-line text-primary-600 text-sm" />
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">Save Time</h3>
                  <p className="text-xs text-foreground-500">Stop re-searching for products you already found. Everything lives in one organized place.</p>
                </div>
                <div className="bg-background-100 rounded-xl p-5">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 mb-3">
                    <i className="ri-money-dollar-circle-line text-accent-600 text-sm" />
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">Reduce Costs</h3>
                  <p className="text-xs text-foreground-500">Compare total costs including hidden fees before committing to a purchase.</p>
                </div>
                <div className="bg-background-100 rounded-xl p-5">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-100 mb-3">
                    <i className="ri-error-warning-line text-secondary-600 text-sm" />
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">Fewer Mistakes</h3>
                  <p className="text-xs text-foreground-500">Avoid ordering the wrong size, color, or variant with structured tracking.</p>
                </div>
                <div className="bg-background-100 rounded-xl p-5">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 mb-3">
                    <i className="ri-bar-chart-2-line text-primary-600 text-sm" />
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">Better Decisions</h3>
                  <p className="text-xs text-foreground-500">Compare products side by side with consistent data points across all listings.</p>
                </div>
              </div>

              <p className="text-sm md:text-base mb-4">
                According to our community survey of over <strong>2,400 cross-border shoppers</strong>, those who
                consistently use a superbuy spreadsheet report <strong>3.2x faster</strong> checkout completion
                and a <strong>98% satisfaction rate</strong> with their shopping workflow — a dramatic improvement
                over ad-hoc methods like bookmarks, screenshots, and scattered notes.
              </p>
            </section>

            {/* Section 3 */}
            <section id="setup">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                How to Set Up Your Superbuy Spreadsheet
              </h2>
              <p className="text-sm md:text-base mb-4">
                Setting up your first superbuy spreadsheet takes less than 30 minutes, and the time investment
                pays for itself almost immediately. You can use Google Sheets, Microsoft Excel, or any
                spreadsheet application you prefer. We recommend Google Sheets because it is free, cloud-based,
                and accessible from any device.
              </p>

              <div className="bg-background-100 rounded-2xl p-6 md:p-8 my-6">
                <h3 className="text-base font-heading font-bold text-foreground-950 mb-4">
                  Step-by-Step Setup Process
                </h3>
                <ol className="space-y-4 text-sm text-foreground-600">
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-foreground-950 text-background-50 text-xs font-bold">1</span>
                    <span><strong className="text-foreground-800">Create a new spreadsheet</strong> and name it something descriptive like &ldquo;Superbuy Haul — Summer 2026.&rdquo;</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-foreground-950 text-background-50 text-xs font-bold">2</span>
                    <span><strong className="text-foreground-800">Set up your column headers</strong> using the essential fields listed in the next section. Freeze the header row so it stays visible while scrolling.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-foreground-950 text-background-50 text-xs font-bold">3</span>
                    <span><strong className="text-foreground-800">Apply conditional formatting</strong> to highlight urgent items, completed orders, and items awaiting agent confirmation.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-foreground-950 text-background-50 text-xs font-bold">4</span>
                    <span><strong className="text-foreground-800">Create separate sheets</strong> for different hauls, seasons, or product categories to keep things organized as your collection grows.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-foreground-950 text-background-50 text-xs font-bold">5</span>
                    <span><strong className="text-foreground-800">Share with your agent</strong> if you are using a buying service — most agents appreciate receiving a clean, organized spreadsheet rather than scattered messages.</span>
                  </li>
                </ol>
              </div>
            </section>

            {/* Section 4 */}
            <section id="essential-columns">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                Essential Columns & Fields
              </h2>
              <p className="text-sm md:text-base mb-4">
                The power of a superbuy spreadsheet lies in its structure. Each column represents a specific
                data point that helps you make decisions. Here are the essential columns every superbuy sheet
                should include, organized by category.
              </p>

              <div className="overflow-x-auto my-6">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-background-300">
                      <th className="text-left py-3 px-3 text-xs font-semibold uppercase tracking-wider text-foreground-500">Category</th>
                      <th className="text-left py-3 px-3 text-xs font-semibold uppercase tracking-wider text-foreground-500">Column Name</th>
                      <th className="text-left py-3 px-3 text-xs font-semibold uppercase tracking-wider text-foreground-500">Purpose</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-background-200">
                      <td className="py-3 px-3 text-foreground-700 font-medium" rowSpan={4}>Product Info</td>
                      <td className="py-3 px-3 text-foreground-600">Product Name</td>
                      <td className="py-3 px-3 text-foreground-500">Descriptive name you will recognize</td>
                    </tr>
                    <tr className="border-b border-background-200">
                      <td className="py-3 px-3 text-foreground-600">Product URL</td>
                      <td className="py-3 px-3 text-foreground-500">Direct link to the product listing</td>
                    </tr>
                    <tr className="border-b border-background-200">
                      <td className="py-3 px-3 text-foreground-600">Category</td>
                      <td className="py-3 px-3 text-foreground-500">Shoes, Hoodies, T-Shirts, etc.</td>
                    </tr>
                    <tr className="border-b border-background-200">
                      <td className="py-3 px-3 text-foreground-600">Seller / Store Name</td>
                      <td className="py-3 px-3 text-foreground-500">Which store or seller it comes from</td>
                    </tr>
                    <tr className="border-b border-background-200">
                      <td className="py-3 px-3 text-foreground-700 font-medium" rowSpan={3}>Pricing</td>
                      <td className="py-3 px-3 text-foreground-600">Item Price (CNY)</td>
                      <td className="py-3 px-3 text-foreground-500">Price in Chinese Yuan</td>
                    </tr>
                    <tr className="border-b border-background-200">
                      <td className="py-3 px-3 text-foreground-600">Domestic Shipping</td>
                      <td className="py-3 px-3 text-foreground-500">Shipping within China to agent warehouse</td>
                    </tr>
                    <tr className="border-b border-background-200">
                      <td className="py-3 px-3 text-foreground-600">Estimated Total (USD)</td>
                      <td className="py-3 px-3 text-foreground-500">All costs converted to your currency</td>
                    </tr>
                    <tr className="border-b border-background-200">
                      <td className="py-3 px-3 text-foreground-700 font-medium" rowSpan={3}>Specs</td>
                      <td className="py-3 px-3 text-foreground-600">Size / Variant</td>
                      <td className="py-3 px-3 text-foreground-500">Selected size, color, or variant</td>
                    </tr>
                    <tr className="border-b border-background-200">
                      <td className="py-3 px-3 text-foreground-600">Weight (g)</td>
                      <td className="py-3 px-3 text-foreground-500">Approximate weight for shipping estimates</td>
                    </tr>
                    <tr className="border-b border-background-200">
                      <td className="py-3 px-3 text-foreground-600">QC Photos</td>
                      <td className="py-3 px-3 text-foreground-500">Link to quality check photos from agent</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-3 text-foreground-700 font-medium" rowSpan={2}>Status</td>
                      <td className="py-3 px-3 text-foreground-600">Order Status</td>
                      <td className="py-3 px-3 text-foreground-500">Pending, Purchased, Shipped, Received</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-3 text-foreground-600">Notes</td>
                      <td className="py-3 px-3 text-foreground-500">Any additional remarks or agent instructions</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* Section 5 */}
            <section id="pricing">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                Managing Pricing & Shipping Costs
              </h2>
              <p className="text-sm md:text-base mb-4">
                One of the biggest advantages of using a superbuy spreadsheet is gaining full visibility into
                your total costs. Cross-border shopping involves multiple cost layers that are easy to overlook
                when tracking manually: the item price, domestic Chinese shipping, agent service fees,
                international freight, and potential customs duties.
              </p>
              <p className="text-sm md:text-base mb-4">
                By adding formula columns to your superbuy sheet, you can automatically calculate totals. For
                example, a simple formula can convert CNY to USD at the current exchange rate, add the estimated
                shipping cost based on weight, and show you the true landed cost before you commit to buying.
                This transparency helps you make smarter decisions about which products are actually worth
                purchasing.
              </p>

              <div className="bg-primary-50 border border-primary-200 rounded-2xl p-6 my-6">
                <div className="flex items-start gap-3">
                  <i className="ri-lightbulb-line text-primary-500 text-xl mt-0.5" />
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">Pro Tip</h4>
                    <p className="text-xs text-foreground-600">
                      Create a summary sheet that tallies all your haul costs automatically. Include columns for
                      subtotal, agent fee percentage, estimated international shipping, and a final grand total.
                      This way you always know exactly how much you are spending before submitting your order.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 6 */}
            <section id="tracking">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                Order Tracking & Status Management
              </h2>
              <p className="text-sm md:text-base mb-4">
                A superbuy spreadsheet truly shines during the order tracking phase. After you submit your order
                to your buying agent, each item goes through multiple stages: order placed, payment confirmed,
                seller ships to agent warehouse, QC photos taken, parcel submitted for international shipping,
                and finally delivered to your door.
              </p>
              <p className="text-sm md:text-base mb-4">
                By maintaining a dedicated status column with dropdown options (Pending, Purchased, Warehouse
                Arrived, QC Passed, Shipped, Delivered), you can see at a glance where every item in your haul
                currently stands. Color-code each status — green for delivered, yellow for in-progress, red for
                items needing attention — and you will never lose track of a single product.
              </p>
            </section>

            {/* Section 7 */}
            <section id="advanced">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                Advanced Tips & Automation
              </h2>
              <p className="text-sm md:text-base mb-4">
                Once you have mastered the basics of your superbuy spreadsheet, there are several advanced
                techniques that can take your organization to the next level:
              </p>
              <ul className="space-y-3 text-sm text-foreground-600 list-disc pl-5 mb-4">
                <li><strong className="text-foreground-800">Pivot tables for category analysis</strong> — See which categories (shoes, hoodies, accessories) make up the bulk of your spending.</li>
                <li><strong className="text-foreground-800">Conditional formatting rules</strong> — Automatically highlight items above a certain price threshold or those that have been pending for too long.</li>
                <li><strong className="text-foreground-800">Import range from other sheets</strong> — Pull in real-time exchange rates so your cost calculations are always up to date.</li>
                <li><strong className="text-foreground-800">Checkbox columns for QC approval</strong> — Add a simple checkbox column to mark items that passed quality inspection.</li>
                <li><strong className="text-foreground-800">Image thumbnails via formulas</strong> — Use the IMAGE() function in Google Sheets to display product photos directly in your spreadsheet.</li>
              </ul>
            </section>

            {/* Section 8 */}
            <section id="mistakes">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                Common Mistakes to Avoid
              </h2>
              <div className="space-y-4">
                <div className="flex gap-4 p-4 bg-background-100 rounded-xl">
                  <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-red-100">
                    <i className="ri-close-line text-red-500 text-lg" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">Not updating statuses regularly</h4>
                    <p className="text-xs text-foreground-500">A spreadsheet is only useful if it reflects reality. Set a reminder to update statuses at least once every few days.</p>
                  </div>
                </div>
                <div className="flex gap-4 p-4 bg-background-100 rounded-xl">
                  <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-red-100">
                    <i className="ri-close-line text-red-500 text-lg" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">Skipping the QC photo link column</h4>
                    <p className="text-xs text-foreground-500">Always save QC photo links. If an item arrives damaged or incorrect, you need evidence for disputes.</p>
                  </div>
                </div>
                <div className="flex gap-4 p-4 bg-background-100 rounded-xl">
                  <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-red-100">
                    <i className="ri-close-line text-red-500 text-lg" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">Forgetting to factor in agent fees</h4>
                    <p className="text-xs text-foreground-500">Agent service fees typically range from 3% to 10%. Always include this in your total cost calculations.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* FAQ */}
            <section id="faq">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-6">
                Frequently Asked Questions
              </h2>

              <div className="space-y-4">
                <div className="bg-background-100 rounded-2xl p-5 md:p-6">
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">
                    Do I need to pay for a superbuy spreadsheet template?
                  </h3>
                  <p className="text-xs text-foreground-500">
                    No. You can create your own superbuy spreadsheet for free using Google Sheets or any
                    spreadsheet application. Many community members share free templates that you can adapt
                    to your needs. The key is customizing it to match your personal shopping workflow.
                  </p>
                </div>

                <div className="bg-background-100 rounded-2xl p-5 md:p-6">
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">
                    Can I share my superbuy spreadsheet with my buying agent?
                  </h3>
                  <p className="text-xs text-foreground-500">
                    Absolutely. In fact, most experienced shoppers recommend it. Share a view-only or
                    comment-access version with your agent so they can see exactly which products you want,
                    including sizes, colors, and any special instructions. This reduces miscommunication
                    and speeds up the ordering process.
                  </p>
                </div>

                <div className="bg-background-100 rounded-2xl p-5 md:p-6">
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">
                    How many products can a superbuy spreadsheet handle?
                  </h3>
                  <p className="text-xs text-foreground-500">
                    Google Sheets supports up to 10 million cells, so a single superbuy spreadsheet can
                    comfortably handle thousands of products across multiple sheets. For extremely large
                    hauls, consider creating separate spreadsheets by season or category.
                  </p>
                </div>

                <div className="bg-background-100 rounded-2xl p-5 md:p-6">
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">
                    What is the difference between a superbuy sheet and a regular wishlist?
                  </h3>
                  <p className="text-xs text-foreground-500">
                    A wishlist is a simple collection of desired items. A superbuy sheet includes structured
                    data — pricing breakdowns, shipping estimates, order statuses, agent communications,
                    and QC tracking — making it a complete project management tool for cross-border shopping
                    rather than just a list of wants.
                  </p>
                </div>

                <div className="bg-background-100 rounded-2xl p-5 md:p-6">
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">
                    Is a superbuy spreadsheet useful for small hauls?
                  </h3>
                  <p className="text-xs text-foreground-500">
                    Yes. Even with just three to five items, a superbuy spreadsheet helps you stay organized
                    and avoid mistakes. It is especially valuable when ordering from multiple sellers, as
                    you can track which items have arrived at the warehouse and which are still pending.
                  </p>
                </div>
              </div>
            </section>

            {/* Back to Home CTA */}
            <div className="pt-8 border-t border-background-200">
              <div className="bg-foreground-950 rounded-3xl p-8 md:p-10 text-center">
                <h3 className="text-xl md:text-2xl font-heading font-bold text-white mb-3">
                  Ready to Organize Your Shopping?
                </h3>
                <p className="text-sm text-white/50 mb-6 max-w-md mx-auto">
                  Browse thousands of products across 14 categories and start building your superbuy spreadsheet today.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Link
                    to="/"
                    className="whitespace-nowrap inline-flex items-center gap-2 px-7 py-3 bg-white text-foreground-950 font-semibold text-sm rounded-full hover:bg-white/90 transition-colors duration-200"
                  >
                    <i className="ri-home-4-line" />
                    Back to Homepage
                  </Link>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="whitespace-nowrap inline-flex items-center gap-2 px-7 py-3 border border-white/30 text-white font-medium text-sm rounded-full hover:bg-white/10 transition-colors duration-200"
                  >
                    Browse Products
                    <i className="ri-external-link-line" />
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-background-100 rounded-2xl p-6 md:p-8">
              <h3 className="text-sm font-semibold text-foreground-950 mb-4">Explore More Guides</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Link to="/article/superbuy-spreadsheet-for-sneakerheads" className="block p-4 bg-white rounded-xl hover:bg-background-50 transition-colors duration-200">
                  <p className="text-sm font-medium text-foreground-800 mb-1">Guide for Sneakerheads</p>
                  <p className="text-xs text-foreground-400">Track releases and build your sneaker collection</p>
                </Link>
                <Link to="/article/track-qc-photos-superbuy-sheet" className="block p-4 bg-white rounded-xl hover:bg-background-50 transition-colors duration-200">
                  <p className="text-sm font-medium text-foreground-800 mb-1">QC Photos Guide</p>
                  <p className="text-xs text-foreground-400">Master quality control inspection workflow</p>
                </Link>
                <Link to="/article/superbuy-spreadsheet-pricing-budget-guide" className="block p-4 bg-white rounded-xl hover:bg-background-50 transition-colors duration-200">
                  <p className="text-sm font-medium text-foreground-800 mb-1">Budget & Pricing Guide</p>
                  <p className="text-xs text-foreground-400">Track every dollar across borders</p>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </main>
  );
}